/**
 * PlotlyChartRender — shared interactive Plotly chart for chat surfaces.
 *
 * Single component used by BOTH:
 *   - the customer artifact canvas (via PlotArtifact when data.plotly is set)
 *   - the admin AI tab's tool-call card (renders inline when the
 *     tool output's artifact_type === "plotly_chart")
 *
 * Responsibilities:
 *   - Mount a Plotly chart from the (data, layout, config) dict the
 *     backend tool produced.
 *   - Provide a download dropdown (PNG only in v1; SVG deferred).
 *   - Caption + accessible alt text.
 *   - Lazy-load plotly.js-dist-min — non-plot routes don't pay the
 *     ~1.2 MB cost on first paint.
 *
 * Why a single component:
 *   - One place to tune layout, palette, watermark behaviour. Bug fixes
 *     apply to admin and customer surfaces simultaneously.
 *   - Plotly's modeBar customisation is non-trivial; centralising
 *     avoids two divergent copies.
 */

import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { applyExplorerHandoff } from './navLinkUtils.js'
// Static `import * as` matches cell-detail/plots/{Energy,Power}DetailView
// which use `Plotly.newPlot` reliably. CRITICAL: we cannot resolve the
// namespace at module-load time — `optimizeDeps.exclude` for
// plotly.js-dist-min in vite.config.js makes the CJS-as-ESM bindings
// populate AFTER importing modules' top-level code runs (CJS interop
// race). Cell-detail works because it touches `Plotly.newPlot` lazily
// inside useEffect; do the same here.
import * as PlotlyNamespace from 'plotly.js-dist-min'

function resolvePlotly() {
  // UMD-shipped libraries attach themselves to `window` as a side
  // effect of being evaluated. Plotly.js does this for legacy <script>
  // tag compatibility, and that path also works when Vite serves the
  // CJS module via on-the-fly transform (where the ESM-namespace
  // bindings can come back empty).
  const candidates = [
    PlotlyNamespace,
    PlotlyNamespace?.default,
    PlotlyNamespace?.default?.default,
    typeof window !== 'undefined' ? window.Plotly : null,
    typeof globalThis !== 'undefined' ? globalThis.Plotly : null,
  ]
  const found = candidates.find(
    (c) => c && (typeof c.newPlot === 'function' || typeof c.react === 'function'),
  )
  if (!found && typeof window !== 'undefined') {
    // Diagnostic — surface what Vite actually delivered so the next
    // failure can be pinned in one console glance.
    console.warn(
      '[PlotlyChartRender] resolvePlotly miss',
      {
        nsKeys: PlotlyNamespace && Object.keys(PlotlyNamespace).slice(0, 12),
        defaultKeys:
          PlotlyNamespace?.default &&
          Object.keys(PlotlyNamespace.default).slice(0, 12),
        hasWindowPlotly: typeof window.Plotly !== 'undefined',
      },
    )
  }
  return found
}

const PNG_DOWNLOAD_SCALE = 2 // ~200 DPI, preserves the forensic watermark on tick decimals

/**
 * @param {object} props
 * @param {object} props.figure — { data: [...], layout: {...}, config: {...} }
 * @param {string} [props.alt]
 * @param {string} [props.caption]
 * @param {string} [props.tool] — originating tool name (for download filename + telemetry)
 * @param {string[]} [props.skippedCells]
 * @param {string[]} [props.chain] — build-chain step lines (fetch → ops → plot);
 *   rendered as an expandable "How this chart was built" panel. The chain IS
 *   the plot's meaning (which cells, which filters/ops); raw arrays + ids stay
 *   admin-only via server-side redaction, so what reaches here is safe to show.
 */
export function PlotlyChartRender({
  figure,
  alt,
  caption,
  tool,
  skippedCells,
  chain,
  // Provenance: true when every datapoint in the figure came from a
  // tool call (Batemo measurement). false when the figure was
  // produced from AI-provided / general-knowledge data. The badge
  // makes the difference glanceable on screen.
  isVerified,
  // `{url, label}` from the backend's `explorer_handoff`, present only on
  // catalogue charts drawn away from the Explorer — see the render below.
  handoff,
}) {
  const [, navigate] = useLocation()
  const containerRef = useRef(null)
  const plotlyRef = useRef(null)
  const [loadError, setLoadError] = useState(null)
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [chainOpen, setChainOpen] = useState(false)

  // Render / re-render whenever the figure dict changes. Resolving
  // Plotly here (lazy) avoids the module-load race with Vite's
  // CJS-as-ESM transform for the excluded plotly.js-dist-min.
  //
  // History-load fix: when a conversation is rehydrated, every message
  // mounts at once while the column is still laying out, so the plot
  // container can have clientWidth === 0. Plotly silently fails to size
  // a zero-width chart and — because this effect only re-runs on
  // `figure` change (never for a static historical message) — it would
  // stay blank forever. We therefore (a) skip rendering until the
  // container has real dimensions, and (b) attach a ResizeObserver that
  // renders as soon as the container is sized (and resizes Plotly on
  // later layout changes — container resizes that `config.responsive`'s
  // window listener doesn't catch). Same path used by SalesRagonePlot.
  useEffect(() => {
    if (!figure || !containerRef.current) return undefined
    const Plotly = plotlyRef.current || resolvePlotly()
    if (!Plotly) {
      setLoadError(
        'Plotly module is missing newPlot/react — incompatible build.',
      )
      return undefined
    }
    plotlyRef.current = Plotly
    if (loadError) setLoadError(null)

    const node = containerRef.current
    let plotted = false

    // Prefer `react` (internal diff, no flicker on re-render); fall
    // back to `newPlot` when react isn't present in the bundle.
    const render =
      typeof Plotly.react === 'function' ? Plotly.react : Plotly.newPlot

    const tryRender = () => {
      // No-op until the container has real dimensions — see comment above.
      if (!node.clientWidth || !node.clientHeight) return
      try {
        render(node, figure.data || [], figure.layout || {}, figure.config || {})
        plotted = true
      } catch (err) {
        console.error('[PlotlyChartRender] render failed:', err)
        setLoadError(err.message || 'Plot render failed')
      }
    }

    // Live conversations: container is already sized, renders instantly.
    tryRender()

    let observer
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (!plotted) {
          tryRender()
        } else {
          try {
            Plotly.Plots.resize(node)
          } catch {
            /* chart purged / node detached */
          }
        }
      })
      observer.observe(node)
    }

    return () => {
      if (observer) observer.disconnect()
      try {
        Plotly.purge(node)
      } catch {
        /* node already detached by parent unmount */
      }
    }
  }, [figure])

  // PNG download via Plotly's client-side toImage. Includes layout.images
  // (the Batemo logo), so the downloaded PNG is branded automatically.
  // scale=2 → ~200 DPI, preserves the per-user forensic watermark
  // encoded in tick-decimal precision.
  const handleDownloadPng = async () => {
    setDownloadOpen(false)
    const Plotly = plotlyRef.current || resolvePlotly()
    if (!Plotly || !containerRef.current) return
    const filename =
      (tool || 'ion-chart') + '-' + new Date().toISOString().slice(0, 10)
    try {
      await Plotly.downloadImage(containerRef.current, {
        format: 'png',
        filename,
        scale: PNG_DOWNLOAD_SCALE,
      })
    } catch (err) {
      console.warn('[PlotlyChartRender] PNG download failed:', err)
    }
  }

  if (!figure) return null
  if (loadError) {
    return (
      <div class="ion-plot-render ion-plot-render--error" role="alert">
        <p class="ion-plot-render__error">
          Couldn’t load the chart library. {loadError}
        </p>
      </div>
    )
  }

  return (
    <figure
      class="ion-plot-render"
      role="figure"
      aria-label={alt || caption || 'Chart'}
    >
      <div class="ion-plot-render__toolbar">
        {/* Single-item dropdown today (Decision: PNG only for v1). The
            UI shape supports adding SVG later without restructuring. */}
        <button
          type="button"
          class="ion-plot-render__download-btn"
          aria-haspopup="menu"
          aria-expanded={downloadOpen}
          onClick={() => setDownloadOpen((v) => !v)}
        >
          Download ▾
        </button>
        {downloadOpen && (
          <ul class="ion-plot-render__download-menu" role="menu">
            <li role="menuitem">
              <button
                type="button"
                class="ion-plot-render__download-item"
                onClick={handleDownloadPng}
              >
                PNG
              </button>
            </li>
            {/* SVG intentionally absent in v1 — exposes raw vector data,
                pending the scraping-protection review. */}
          </ul>
        )}
      </div>

      <div class="ion-plot-render__plot-wrap" style="position:relative">
        <div ref={containerRef} class="ion-plot-render__plot" />
        {/* Provenance warning, top-right of the chart. The positive
            "✓ Batemo" pill was removed (it duplicated the logo + the
            build-chain box); we keep ONLY the negative warning, which
            carries real signal: this chart mixes in AI-provided values
            that are NOT Batemo measurements. Renders only when the
            producer explicitly set is_verified === false. */}
        {isVerified === false && (
          <span
            class="ion-plot-badge ion-plot-badge--unverified"
            title="This chart includes AI-provided values that are NOT from Batemo measurements."
            aria-label="AI-provided data"
          >
            ⚠ AI-provided
          </span>
        )}
      </div>

      {(caption || alt) && (
        <figcaption class="ion-plot-render__caption">
          {caption || alt}
        </figcaption>
      )}

      {/* Handoff into the real Explorer. The backend attaches this only to
          catalogue charts drawn AWAY from the Explorer (plot_scatter's
          `explorer_handoff`), so there is nothing to decide here — if the field
          is present, the button belongs. It used to be the model's job to offer
          a nav_link and the model did it inconsistently: same prompt, button one
          run and none the next.

          `applyNavLink` rather than a bare navigate: it is the only thing that
          applies the query string on arrival, and it clears the previous filter
          set so the Explorer shows what this label names. */}
      {handoff?.url && (
        <div class="ion-plot-handoff">
          <button
            type="button"
            class="ion-nav-link"
            onClick={() => applyExplorerHandoff(handoff, navigate)}
          >
            {handoff.label || 'Open in the Explorer'} ↗
          </button>
        </div>
      )}

      {/* Build-chain provenance — the "what does this plot show / which
          filters were applied" panel. Collapsed by default so it never
          competes with the chart; one click reveals every step. */}
      {chain && chain.length > 0 && (
        <div class="ion-plot-chain">
          <button
            type="button"
            class="ion-plot-chain__toggle"
            aria-expanded={chainOpen}
            onClick={() => setChainOpen((v) => !v)}
          >
            {chainOpen ? '▾' : '▸'} How this chart was built ({chain.length}{' '}
            step{chain.length === 1 ? '' : 's'})
          </button>
          {chainOpen && (
            <ol class="ion-plot-chain__steps">
              {chain.map((step, i) => {
                // Steps may carry a trailing symbolic formula as
                // "<prose>   [<formula>]" (appended server-side by
                // chain_step_lines). Render the maths part in a
                // monospace span — same treatment as the scalar
                // data-chip expression — so the formula reads as a
                // formula, not prose.
                const m = /^(.*?)\s{2,}\[(.+)\]\s*$/.exec(step)
                return (
                  <li key={i} class="ion-plot-chain__step">
                    {m ? (
                      <>
                        {m[1]}
                        <span class="ion-plot-chain__formula">{m[2]}</span>
                      </>
                    ) : (
                      step
                    )}
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      )}

      {skippedCells && skippedCells.length > 0 && (
        <p class="ion-plot-render__notice">
          {skippedCells.length} cell
          {skippedCells.length === 1 ? '' : 's'} skipped (no data).
        </p>
      )}
    </figure>
  )
}
